/**
 * Danger Zone — irreversible super-admin actions.
 *
 * Currently surfaces the engagement-data wipe (production reset).
 * Designed to be safe by construction:
 *   • Visible only to super-admin role.
 *   • Two-stage flow: preview (dry-run) → confirm (typed phrase).
 *   • Confirm button stays disabled until the auditor types the exact
 *     phrase ``WIPE EVERYTHING`` (typo-proof guard).
 *   • Receipt is rendered in-place after the wipe so the auditor can
 *     paste it into their change-log.
 */
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { Warning, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
import { adminWipeEngagements } from "@/lib/api";

const CONFIRM_PHRASE = "WIPE EVERYTHING";


function humanise(bytes) {
  let n = bytes;
  for (const unit of ["B", "KB", "MB", "GB"]) {
    if (n < 1024) return `${n.toFixed(1)} ${unit}`;
    n /= 1024;
  }
  return `${n.toFixed(1)} TB`;
}


export default function DangerZone() {
  const [preview, setPreview] = useState(null);     // dry-run receipt
  const [open, setOpen] = useState(false);          // confirm modal
  const [phrase, setPhrase] = useState("");
  const [busy, setBusy] = useState(false);
  const [receipt, setReceipt] = useState(null);     // post-wipe receipt

  const onPreview = async () => {
    setBusy(true);
    try {
      const r = await adminWipeEngagements("", true);
      setPreview(r);
      setOpen(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load wipe preview");
    } finally { setBusy(false); }
  };

  const onConfirm = async () => {
    if (phrase !== CONFIRM_PHRASE) return;
    setBusy(true);
    try {
      const r = await adminWipeEngagements(CONFIRM_PHRASE, false);
      setReceipt(r);
      setOpen(false);
      setPhrase("");
      setPreview(null);
      toast.success("Engagement data wiped. See the receipt below.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Wipe failed");
    } finally { setBusy(false); }
  };

  return (
    <section
      data-testid="danger-zone"
      className="mt-10 border border-rose-300 bg-rose-50/40 rounded-sm"
    >
      <div className="px-4 py-3 border-b border-rose-200 flex items-center gap-2">
        <Warning size={16} weight="duotone" className="text-rose-700"/>
        <h3 className="font-heading text-base text-rose-900">Danger Zone</h3>
      </div>
      <div className="p-5">
        <div className="font-mono text-[10.5px] uppercase tracking-[0.18em] text-rose-800">
          Wipe ALL engagement data
        </div>
        <p className="text-[13px] text-slate-700 mt-1.5">
          Permanently deletes every client, run, library file and module artefact
          across the workspace.  <strong>Users, sessions, invitations and the
          depreciation legal-master are preserved</strong> — your team&apos;s logins
          stay intact.  Intended as a one-time pre-UAT reset.  This cannot be
          undone.
        </p>

        <div className="mt-4 flex items-center gap-3">
          <Button
            onClick={onPreview}
            disabled={busy}
            variant="outline"
            data-testid="wipe-preview-btn"
            className="rounded-sm shadow-none border-rose-300 text-rose-800 hover:bg-rose-100"
          >
            Preview what will be deleted
          </Button>
        </div>

        {receipt && (
          <div className="mt-6 border border-emerald-200 bg-emerald-50/60 rounded-sm p-4" data-testid="wipe-receipt">
            <div className="font-mono text-[10.5px] uppercase tracking-[0.18em] text-emerald-800">
              Wipe Receipt
            </div>
            <div className="mt-2 text-[12.5px] text-slate-800 grid sm:grid-cols-2 gap-y-1 gap-x-6">
              <div>Triggered by</div><div className="font-mono">{receipt.triggered_by}</div>
              <div>Triggered at</div><div className="font-mono">{receipt.triggered_at}</div>
              <div>Collections dropped</div>
              <div className="font-mono">{receipt.dropped_collections?.length || 0}</div>
              <div>Documents removed</div>
              <div className="font-mono">{receipt.wiped_total_docs?.toLocaleString() || 0}</div>
              <div>Library blobs removed</div>
              <div className="font-mono">
                {receipt.upload_files_before} → {receipt.upload_files_after} files
                {" · "}{humanise(receipt.upload_bytes_before)} → {humanise(receipt.upload_bytes_after)}
              </div>
              <div>Preserved tables intact</div>
              <div className="font-mono">{receipt.preserved_intact ? "YES" : "NO — INVESTIGATE"}</div>
            </div>
            {receipt.drop_failures?.length > 0 && (
              <div className="mt-3 text-[12px] text-rose-800">
                Some drops failed: {receipt.drop_failures.map(f => f.collection).join(", ")}
              </div>
            )}
          </div>
        )}
      </div>

      <AlertDialog open={open} onOpenChange={(o) => { if (!o) { setOpen(false); setPhrase(""); } }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-rose-900 inline-flex items-center gap-2">
              <Trash size={18}/> Confirm wipe — this cannot be undone
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-[13px] text-slate-700">
                <p>You&apos;re about to permanently delete:</p>
                {preview && (
                  <div className="border border-rose-200 bg-rose-50/40 rounded-sm p-3 font-mono text-[11.5px] leading-relaxed max-h-48 overflow-y-auto" data-testid="wipe-preview-list">
                    <div className="text-rose-900 mb-1">
                      {preview.to_wipe_total_docs?.toLocaleString()} documents across {Object.keys(preview.to_wipe).length} collections
                    </div>
                    <div className="text-slate-700">
                      {preview.upload_files} library files ({humanise(preview.upload_bytes)})
                    </div>
                    <div className="mt-2 text-slate-600">
                      Preserved: {Object.entries(preview.preserved)
                        .map(([k, v]) => `${k} (${v})`)
                        .join(", ")}
                    </div>
                  </div>
                )}
                <div>
                  <Label htmlFor="wipe-confirm-input" className="text-[11px] uppercase tracking-[0.12em] font-mono text-rose-900">
                    Type <span className="font-mono bg-rose-100 px-1.5 py-0.5 rounded-sm">{CONFIRM_PHRASE}</span> to enable the button
                  </Label>
                  <Input
                    id="wipe-confirm-input"
                    data-testid="wipe-confirm-input"
                    autoComplete="off"
                    autoFocus
                    value={phrase}
                    onChange={(e) => setPhrase(e.target.value)}
                    placeholder={CONFIRM_PHRASE}
                    className="mt-1 rounded-sm shadow-none border-rose-300 bg-white font-mono"
                  />
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="wipe-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={onConfirm}
              disabled={phrase !== CONFIRM_PHRASE || busy}
              data-testid="wipe-confirm-btn"
              className="bg-rose-700 hover:bg-rose-800 disabled:opacity-50"
            >
              {busy ? "Wiping…" : "Yes, wipe everything"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}
